import { AttentionDot } from "./attention";
import { cn } from "../../app/components/primitives/utils";

// The tabs every customer quote and order page has, in this order and with
// these names (Ben, 5 Oct): Needs you · N, Items · N, Delivery, Price,
// Documents, Notes. The page opens on Needs you when it has anything,
// otherwise on Items; the open tab lives in the URL (`?tab=`) so an email
// can land on one.

export type PageTab =
  "needs" | "items" | "delivery" | "price" | "documents" | "notes";

export const PAGE_TABS: PageTab[] = [
  "needs",
  "items",
  "delivery",
  "price",
  "documents",
  "notes",
];

const LABELS: Record<PageTab, string> = {
  needs: "Needs you",
  items: "Items",
  delivery: "Delivery",
  price: "Price",
  documents: "Documents",
  notes: "Notes",
};

/** The tab to show: the URL's, else Needs you when anything is owed. */
export function resolvePageTab(
  requested: string | null,
  needsCount: number,
): PageTab {
  if (requested && (PAGE_TABS as string[]).includes(requested)) {
    return requested as PageTab;
  }
  return needsCount > 0 ? "needs" : "items";
}

export function PageTabs({
  current,
  needsCount,
  itemsCount,
  itemsNeedAttention = false,
  onSelect,
  label,
}: {
  current: PageTab;
  needsCount: number;
  itemsCount: number;
  /** Any item needs the customer: the trail starts at the tab. */
  itemsNeedAttention?: boolean;
  onSelect: (tab: PageTab) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="-mx-1 flex gap-1 overflow-x-auto border-b"
    >
      {PAGE_TABS.map((t) => {
        const count =
          t === "needs" ? needsCount : t === "items" ? itemsCount : null;
        return (
          <button
            key={t}
            role="tab"
            type="button"
            aria-selected={current === t}
            onClick={() => onSelect(t)}
            className={cn(
              "flex shrink-0 items-center whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors",
              current === t
                ? "border-primary font-semibold text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {LABELS[t]}
            {t === "items" && itemsNeedAttention && (
              <AttentionDot className="ml-1.5" label="An item needs you" />
            )}
            {count !== null && (
              <span
                className={cn(
                  "ml-1.5 rounded-full px-1.5 py-0.5 text-xs",
                  t === "needs" && count > 0
                    ? "bg-warning text-warning-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
